import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { trainingApi } from "../../services/trainingApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { NotFoundState } from "../../components/NotFoundState";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

const STATUS_FLOW = ["DRAFT", "REVIEW_REQUIRED", "UNDER_REVIEW", "APPROVED", "PUBLISHED"];
const TABS = ["overview", "lessons", "quizzes"];

export default function TrainingCourseEditorPage() {
  const { courseId } = useParams();
  const { t } = useLocalization();
  const { canViewTraining, canManageTrainingContent, canApproveTrainingContent, canPublishTraining } = usePermissions();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("overview");
  const [lessonForm, setLessonForm] = useState({ title_en: "", title_ja: "", order: 0, content: "" });
  const [quizForm, setQuizForm] = useState({ key: "", title_en: "", title_ja: "", proposed_pass_score: 90 });
  const [questionForms, setQuestionForms] = useState({});
  const [questionsByBank, setQuestionsByBank] = useState({});

  const TAB_LABELS = { overview: t("mgmtCourseEditor.tabOverview"), lessons: t("mgmtCourseEditor.tabLessons"), quizzes: t("mgmtCourseEditor.tabQuizzes") };

  const load = useCallback(() => {
    trainingApi.getCourse(courseId).then((r) => { setCourse(r.data); setNotFound(false); }).catch((err) => {
      if (err.response?.status === 404) setNotFound(true);
    });
  }, [courseId]);

  useEffect(() => { if (canViewTraining) load(); }, [canViewTraining, load]);

  useEffect(() => {
    if (!course?.quiz_banks) return;
    course.quiz_banks.forEach((b) => {
      trainingApi.listQuestions(b.id).then((r) => setQuestionsByBank((prev) => ({ ...prev, [b.id]: r.data }))).catch(() => {});
    });
  }, [course]);

  if (!canViewTraining) return <p className="text-sm text-gray-500" data-testid="course-editor-access-denied">{t("common.accessDenied")}</p>;
  if (notFound) return <NotFoundState testId="training-course-not-found" backTo="/management/training/courses" />;
  if (!course) return <p className="text-sm text-gray-500">{t("common.loading")}</p>;

  const isEditable = course.status === "DRAFT";

  const addLesson = async () => {
    if (!lessonForm.title_en) { toast.error(t("mgmtCourseEditor.lessonTitleRequired")); return; }
    try {
      await trainingApi.addLesson(courseId, {
        title_en: lessonForm.title_en, title_ja: lessonForm.title_ja || lessonForm.title_en, order: Number(lessonForm.order) || 0,
        blocks: [{ type: "text", text_en: lessonForm.content, text_ja: lessonForm.content }],
      });
      toast.success(t("mgmtCourseEditor.lessonAdded"));
      setLessonForm({ title_en: "", title_ja: "", order: 0, content: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createQuizBank = async () => {
    if (!quizForm.key || !quizForm.title_en) { toast.error(t("mgmtCourseEditor.quizKeyTitleRequired")); return; }
    try {
      await trainingApi.createQuizBank({ ...quizForm, title_ja: quizForm.title_ja || quizForm.title_en, course_id: courseId });
      toast.success(t("mgmtCourseEditor.quizBankCreated"));
      setQuizForm({ key: "", title_en: "", title_ja: "", proposed_pass_score: 90 });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addQuestion = async (bankId) => {
    const q = questionForms[bankId] || {};
    if (!q.text_en || !q.optA || !q.optB) { toast.error(t("mgmtCourseEditor.questionTextOptionsRequired")); return; }
    try {
      const options = [
        { text_en: q.optA, text_ja: q.optA, is_correct: q.correct === "A" },
        { text_en: q.optB, text_ja: q.optB, is_correct: q.correct === "B" },
        ...(q.optC ? [{ text_en: q.optC, text_ja: q.optC, is_correct: q.correct === "C" }] : []),
      ];
      await trainingApi.addQuestion(bankId, { question_type: "MULTIPLE_CHOICE", text_en: q.text_en, text_ja: q.text_en, options });
      toast.success(t("mgmtCourseEditor.questionAdded"));
      setQuestionForms((prev) => ({ ...prev, [bankId]: {} }));
      trainingApi.listQuestions(bankId).then((r) => setQuestionsByBank((prev) => ({ ...prev, [bankId]: r.data })));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const moveStatus = async (target) => {
    try { await trainingApi.moveCourseStatus(courseId, target); toast.success(`${t("mgmtCourseEditor.courseMovedTo")} ${target}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const newVersion = async () => {
    try { await trainingApi.newCourseVersion(courseId); toast.success(t("mgmtCourseEditor.newDraftVersionCreated")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const nextStatusIndex = STATUS_FLOW.indexOf(course.status) + 1;
  const nextStatus = STATUS_FLOW[nextStatusIndex];
  const canAdvance = nextStatus === "REVIEW_REQUIRED" ? canManageTrainingContent
    : nextStatus === "UNDER_REVIEW" || nextStatus === "APPROVED" ? canApproveTrainingContent
    : nextStatus === "PUBLISHED" ? canPublishTraining : false;

  return (
    <div className="space-y-5" data-testid="course-editor-page">
      <PageHelpBanner pageKey="course-editor" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtCourseEditor.help") }} />
      </PageHelpBanner>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{course.title_en}</h2>
          <p className="text-sm text-gray-500">{course.key} · v{course.current_version?.version_number}</p>
        </div>
        <StatusBadge status={course.status} />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap gap-2" data-testid="course-status-actions">
        {nextStatus && (
          <button data-testid={`advance-status-btn-${nextStatus}`} disabled={!canAdvance} onClick={() => moveStatus(nextStatus)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
            {t("mgmtCorrectiveAction.moveTo")} {nextStatus}
          </button>
        )}
        {course.status !== "DRAFT" && course.status !== "PUBLISHED" && canManageTrainingContent && (
          <button data-testid="revert-to-draft-btn" onClick={() => moveStatus("DRAFT")} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("mgmtCourseEditor.sendBackToDraft")}</button>
        )}
        {(course.status === "PUBLISHED" || course.status === "REPLACED") && canManageTrainingContent && (
          <button data-testid="new-version-btn" onClick={newVersion} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("mgmtCourseEditor.createNewVersion")}</button>
        )}
        {course.status === "PUBLISHED" && canManageTrainingContent && (
          <button data-testid="archive-course-btn" onClick={() => moveStatus("ARCHIVED")} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50">{t("mgmtCourseEditor.archive")}</button>
        )}
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        {TABS.map((tid) => (
          <button key={tid} data-testid={`course-tab-${tid}`} onClick={() => setTab(tid)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === tid ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-800"}`}>
            {TAB_LABELS[tid]}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2 text-sm" data-testid="course-overview-panel">
          <p><span className="text-gray-500">{t("mgmtCourseEditor.passScore")}:</span> {course.pass_score || 90}%</p>
          <p><span className="text-gray-500">{t("mgmtCourseEditor.practicalRequirement")}:</span> {course.has_practical_requirement ? t("mgmtTrainingCert.yes") : t("mgmtTrainingCert.no")}</p>
          <p><span className="text-gray-500">{t("mgmtCourseEditor.description")}:</span> {course.description_en || "-"}</p>
        </div>
      )}

      {tab === "lessons" && (
        <div className="space-y-3">
          {canManageTrainingContent && isEditable && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="add-lesson-form">
              <input data-testid="lesson-title-input" value={lessonForm.title_en} onChange={(e) => setLessonForm({ ...lessonForm, title_en: e.target.value })} placeholder={t("mgmtCourseEditor.lessonTitlePlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
              <input data-testid="lesson-order-input" type="number" value={lessonForm.order} onChange={(e) => setLessonForm({ ...lessonForm, order: e.target.value })} placeholder={t("mgmtCourseEditor.orderPlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
              <input data-testid="lesson-content-input" value={lessonForm.content} onChange={(e) => setLessonForm({ ...lessonForm, content: e.target.value })} placeholder={t("mgmtCourseEditor.lessonContentPlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm md:col-span-1" />
              <button data-testid="add-lesson-btn" onClick={addLesson} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">{t("mgmtCourseEditor.addLesson")}</button>
            </div>
          )}
          {!isEditable && <p className="text-xs text-amber-700" data-testid="lessons-locked-notice">{t("mgmtCourseEditor.versionLockedNotice")} {course.status} - {t("mgmtCourseEditor.lessonsLockedSuffix")}</p>}
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {(course.lessons || []).map((l) => (
              <div key={l.id} className="p-4" data-testid={`lesson-row-${l.id}`}>
                <p className="text-sm font-semibold text-gray-800">{l.order}. {l.title_en}</p>
                {(l.blocks || []).map((b, i) => <p key={i} className="text-xs text-gray-500 mt-1">{b.text_en}</p>)}
              </div>
            ))}
            {(course.lessons || []).length === 0 && <p className="p-6 text-center text-sm text-gray-500" data-testid="no-lessons-row">{t("mgmtCourseEditor.noLessonsYet")}</p>}
          </div>
        </div>
      )}

      {tab === "quizzes" && (
        <div className="space-y-3">
          {canManageTrainingContent && isEditable && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="add-quiz-bank-form">
              <input data-testid="quiz-key-input" value={quizForm.key} onChange={(e) => setQuizForm({ ...quizForm, key: e.target.value.toUpperCase().replace(/\s/g, "_") })} placeholder={t("mgmtCourseEditor.quizKeyPlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
              <input data-testid="quiz-title-input" value={quizForm.title_en} onChange={(e) => setQuizForm({ ...quizForm, title_en: e.target.value })} placeholder={t("mgmtCourseEditor.quizTitlePlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
              <input data-testid="quiz-pass-score-input" type="number" value={quizForm.proposed_pass_score} onChange={(e) => setQuizForm({ ...quizForm, proposed_pass_score: Number(e.target.value) })} placeholder={t("mgmtTraining.passScorePlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
              <button data-testid="create-quiz-bank-btn" onClick={createQuizBank} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">{t("mgmtCourseEditor.createQuizBank")}</button>
            </div>
          )}
          {(course.quiz_banks || []).map((b) => (
            <div key={b.id} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid={`quiz-bank-row-${b.id}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-800">{b.title_en} ({b.key})</p>
                <span className="text-xs text-gray-500">{t("mgmtCourseEditor.pass")}: {b.proposed_pass_score}% · {b.is_locked ? t("mgmtCourseEditor.lockedInUse") : t("mgmtCourseEditor.editable")}</span>
              </div>
              <div className="space-y-1">
                {(questionsByBank[b.id] || []).map((q) => (
                  <p key={q.id} className="text-xs text-gray-600" data-testid={`question-row-${q.id}`}>{t("mgmtCourseEditor.qPrefix")}: {q.text_en} ({(q.options || []).length} {t("mgmtCourseEditor.options")})</p>
                ))}
                {(questionsByBank[b.id] || []).length === 0 && <p className="text-xs text-gray-500" data-testid={`no-questions-${b.id}`}>{t("mgmtCourseEditor.noQuestionsYet")}</p>}
              </div>
              {canManageTrainingContent && !b.is_locked && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 pt-2 border-t border-gray-100" data-testid={`add-question-form-${b.id}`}>
                  <input data-testid={`question-text-input-${b.id}`} value={questionForms[b.id]?.text_en || ""} onChange={(e) => setQuestionForms((p) => ({ ...p, [b.id]: { ...p[b.id], text_en: e.target.value } }))} placeholder={t("mgmtCourseEditor.questionTextPlaceholder")} className="border border-gray-200 rounded-lg px-2 py-1 text-xs col-span-2" />
                  <input data-testid={`question-opta-input-${b.id}`} value={questionForms[b.id]?.optA || ""} onChange={(e) => setQuestionForms((p) => ({ ...p, [b.id]: { ...p[b.id], optA: e.target.value } }))} placeholder={t("mgmtCourseEditor.optionAPlaceholder")} className="border border-gray-200 rounded-lg px-2 py-1 text-xs" />
                  <input data-testid={`question-optb-input-${b.id}`} value={questionForms[b.id]?.optB || ""} onChange={(e) => setQuestionForms((p) => ({ ...p, [b.id]: { ...p[b.id], optB: e.target.value } }))} placeholder={t("mgmtCourseEditor.optionBPlaceholder")} className="border border-gray-200 rounded-lg px-2 py-1 text-xs" />
                  <select data-testid={`question-correct-select-${b.id}`} value={questionForms[b.id]?.correct || "A"} onChange={(e) => setQuestionForms((p) => ({ ...p, [b.id]: { ...p[b.id], correct: e.target.value } }))} className="border border-gray-200 rounded-lg px-2 py-1 text-xs">
                    <option value="A">{t("mgmtCourseEditor.aCorrect")}</option>
                    <option value="B">{t("mgmtCourseEditor.bCorrect")}</option>
                  </select>
                  <button data-testid={`add-question-btn-${b.id}`} onClick={() => addQuestion(b.id)} className="px-2 py-1 rounded text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 col-span-2">{t("mgmtCourseEditor.addQuestion")}</button>
                </div>
              )}
            </div>
          ))}
          {(course.quiz_banks || []).length === 0 && <p className="text-sm text-gray-500 text-center py-6" data-testid="no-quiz-banks-row">{t("mgmtCourseEditor.noQuizBanksYet")}</p>}
        </div>
      )}
    </div>
  );
}
